import React, { useMemo, useState } from "react";
import {
    GoogleMap,
    Polygon,
    Circle,
    Rectangle,
    Marker,
} from "@react-google-maps/api";
import { GEO_TYPE } from "../../config/constant";

import { Form, Icon, Tabs } from "@/components";
import {
    Autocomplete,
    Button,
    FormLayout,
    Heading,
    Input,
    LoadingProgress,
    Select,
    Switch,
    Textarea,
    TimePicker,
    Tooltip,
} from "@/ui";
import { currencyMask } from "../../utils/maskInput";
import GoogleMapComponent from "./GoogleMapComponent";
import WorkingHour from "base/components/workingHour/WorkingHour";
import { DAYSOFWEEK } from "config/constant";
import { useScheduleContext, useStoreContext } from "context";
import styles from "./ScheduleForm.module.scss";

const ScheduleForm = ({ handleCancel }) => {
    const {
        context,
        inputs,
        setInputs,
        inputChange,
        selectChange,
        activeTab,
        setActiveTab,
        //disableTimeWindows,
        //clearTimeSlotsForAllDay,
        isUpdating,
        isCreate,
        showInvalidMessage,
        getActiveMessage,
        validator,
        pickupValidator,
        createOrUpdate,
        getDayAndMonth,
        inventories,
        loadingInventories,
        storeCoordinates,
        handleStoreProviderChange,
    } = useScheduleContext();
    const { providers } = useStoreContext();
    const [modal, setModal] = useState(false);
    const [isFormReset, resetFormInputs] = useState(isCreate);

    const isDelivery = context === "Delivery";
    const isCurbside = context === "Curbside";
    const googleMapKey = process?.env?.REACT_APP_GOOGLE_MAP_API_KEY;

    const toggleModal = () => {
        setModal(!modal);
    };

    const getValidator = () => (isDelivery ? validator : pickupValidator);
    const fieldValidator = getValidator();
    const fieldError = name =>
        fieldValidator?.[name]?.isInvalid
            ? fieldValidator[name].message
            : undefined;

    const storeSelectOptions = useMemo(
        () =>
            (providers || [])
                .filter(option => option?.value != null && option.value !== "")
                .map(option => ({
                    value: String(option.value),
                    label: option.label ?? String(option.value),
                })),
        [providers],
    );

    const geoTypeOptions = useMemo(
        () =>
            GEO_TYPE.map(option => ({
                value: String(option.value),
                label: option.label,
            })),
        [],
    );

    const getTitle = () => {
        let titleContext = "Pickup Schedule";
        if (isDelivery) {
            titleContext = "Delivery Zone";
        } else if (isCurbside) {
            titleContext = "Curbside Schedule";
        }
        return isCreate ? `Add ${titleContext}` : `Update ${titleContext}`;
    };

    if (!isCreate && !isFormReset) {
        resetFormInputs(true);
    }

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const renderDaysOfWeekTabs = () =>
        DAYSOFWEEK.map(day => {
            const date = getDayAndMonth(day);
            return {
                title: day,
                date: `${date.month}/${date.day}`,
                content: <WorkingHour day={day} key={day} />,
            };
        });

    const handleExpressActiveToggle = checked => {
        setInputs(prev => {
            if (!checked) {
                const updated = [...(prev?.time_slots ?? [])].map(s => ({
                    ...s,
                    is_express: false,
                }));
                return { ...prev, active: false, time_slots: updated };
            }
            return { ...prev, active: true };
        });
    };

    // Inventories multi-select helpers
    const onChangeInventoryMulti = ids => {
        setInputs(prev => ({
            ...prev,
            inventory_provider_ids: ids ?? [],
        }));
    };

    const inventoryOptions = useMemo(
        () =>
            (inventories ?? []).map(op => ({
                label: op.label,
                value: String(op.value),
            })),
        [inventories],
    );

    const inventorySelectedValues = useMemo(() => {
        const ids = Array.isArray(inputs?.inventory_provider_ids)
            ? inputs.inventory_provider_ids
            : [];
        return ids.map(String);
    }, [inputs?.inventory_provider_ids]);

    // Geo helpers
    const getAreaSelected = () => {
        let distance = 0;
        if (inputs?.radius) distance = Number(inputs.radius) * 1609.34;
        return {
            center: { lat: storeCoordinates?.lat, lng: storeCoordinates?.lng },
            radius: distance,
            type: "circle",
        };
    };

    const handleSelectGeofence = geoJsonData => {
        setInputs(prev => ({
            ...prev,
            polygon_geo_zone: JSON.stringify(geoJsonData),
        }));
        toggleModal();
    };

    // Inline preview (optional)
    const GoogleMapPreview = ({ selectedArea }) => {
        const containerStyle = { width: "100%", height: "100%" };
        const optionStyle = {
            fillColor: "lightblack",
            fillOpacity: 0.4,
            strokeColor: "black",
            strokeOpacity: 1.0,
            strokeWeight: 2,
        };
        const defaultUbication = {
            lat: storeCoordinates?.lat ?? 36.7783,
            lng: storeCoordinates?.lng ?? -119.4179,
        };

        const parseArea =
            typeof selectedArea === "string" && selectedArea !== ""
                ? JSON.parse(selectedArea)
                : selectedArea;

        const type = parseArea?.type ?? "";
        let shape = null;
        let centerPoint = null;
        let radius = 1000;

        if (type === "circle") {
            centerPoint = {
                lat: parseArea?.center?.lat,
                lng: parseArea?.center?.lng,
            };
            radius = parseArea?.radius;
        } else if (type === "rectangle") {
            shape = {
                north: parseArea?.north,
                south: parseArea?.south,
                east: parseArea?.east,
                west: parseArea?.west,
            };
        } else {
            shape = parseArea;
        }

        const centerMap =
            type === "circle"
                ? centerPoint
                : type === "rectangle"
                ? {
                      lat: (parseArea?.south + parseArea?.north) / 2,
                      lng: (parseArea?.west + parseArea?.east) / 2,
                  }
                : shape?.[0];

        const center = {
            lat: centerMap?.lat ?? defaultUbication?.lat,
            lng: centerMap?.lng ?? defaultUbication?.lng,
        };

        const markerOptions = {
            position: {
                lat: defaultUbication?.lat,
                lng: defaultUbication?.lng,
            },
        };

        const renderShape = () => {
            if (type === "rectangle" && shape)
                return <Rectangle bounds={shape} options={optionStyle} />;
            if (type === "circle" && centerPoint)
                return (
                    <Circle
                        center={centerPoint}
                        options={optionStyle}
                        radius={radius}
                    />
                );
            if (shape) return <Polygon options={optionStyle} paths={shape} />;
            return null;
        };

        return (
            <GoogleMap
                center={center}
                mapContainerStyle={containerStyle}
                zoom={9}
            >
                {renderShape()}
                <Marker {...markerOptions} />
            </GoogleMap>
        );
    };

    return (
        <Form className={`${styles.root} mb-4`}>
            <FormLayout>
                <Heading level={5}>{getTitle()}</Heading>
                <FormLayout.Section title="Informations">
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Input
                            error={fieldError("name")}
                            fullWidth
                            label="Name"
                            name="name"
                            onChange={inputChange}
                            placeholder="Name"
                            value={inputs.name ?? ""}
                        />
                        <Select
                            error={fieldError("stores_provider_id")}
                            fullWidth
                            label="Store Provider Id"
                            onValueChange={value => {
                                const option = (providers || []).find(
                                    store => String(store.value) === value,
                                );
                                if (option) {
                                    handleStoreProviderChange(option);
                                }
                            }}
                            options={storeSelectOptions}
                            value={
                                inputs.stores_provider_id != null
                                    ? String(inputs.stores_provider_id)
                                    : undefined
                            }
                        />
                    </FormLayout.Grid>
                    {isDelivery ? (
                        <div className={styles.geoLayout}>
                            <div className={styles.geoMain}>
                                <Select
                                    fullWidth
                                    label="Geo Type"
                                    onValueChange={value =>
                                        selectChange({ value }, "geo_type")
                                    }
                                    options={geoTypeOptions}
                                    value={
                                        inputs.geo_type != null
                                            ? String(inputs.geo_type)
                                            : undefined
                                    }
                                />
                                {inputs?.geo_type === "RADIUS" ? (
                                    <Input
                                        fullWidth
                                        label="Distance"
                                        min={1}
                                        name="radius"
                                        onChange={inputChange}
                                        placeholder="Maximum distance for delivery..."
                                        suffix="Mi"
                                        type="number"
                                        value={inputs.radius ?? 1}
                                    />
                                ) : null}
                                {inputs?.geo_type === "GEOFENCE" ? (
                                    <Textarea
                                        fieldClassName={styles.geoTextareaField}
                                        fullWidth
                                        label="Selected area"
                                        name="polygon_geo_zone"
                                        onChange={inputChange}
                                        rows={6}
                                        value={inputs.polygon_geo_zone ?? ""}
                                    />
                                ) : null}
                                <Textarea
                                    fullWidth
                                    label="Note"
                                    name="note"
                                    onChange={inputChange}
                                    rows={5}
                                    value={inputs.note ?? ""}
                                />
                            </div>
                            <div className={styles.geoSide}>
                                {inputs?.geo_type === "ZIP" ? (
                                    <Textarea
                                        className={styles.zipsField}
                                        fieldClassName={styles.zipsFieldInner}
                                        fullWidth
                                        label="Zips"
                                        name="zips"
                                        onChange={inputChange}
                                        rows={5}
                                        textareaClassName={styles.zipsTextarea}
                                        value={inputs.zips ?? ""}
                                    />
                                ) : null}
                                {inputs?.geo_type !== "ZIP" ? (
                                    <div className={styles.geoReference}>
                                        <span className={styles.noteLabel}>
                                            GEO REFERENCE
                                        </span>
                                        <span className={styles.geoHint}>
                                            *The store address is being taken as
                                            center of the area
                                        </span>
                                        <div className={styles.geoMap}>
                                            <GoogleMapPreview
                                                coordinate={storeCoordinates}
                                                key={googleMapKey}
                                                selectedArea={
                                                    inputs?.geo_type ===
                                                    "RADIUS"
                                                        ? getAreaSelected()
                                                        : inputs?.polygon_geo_zone
                                                }
                                            />
                                            {!googleMapKey ? (
                                                <div
                                                    className={
                                                        styles.invalidMessage
                                                    }
                                                    role="alert"
                                                >
                                                    Google Map URL not
                                                    configured
                                                </div>
                                            ) : null}
                                        </div>
                                        {inputs?.geo_type === "GEOFENCE" ? (
                                            <div className={styles.geoActions}>
                                                <Button
                                                    disabled={!googleMapKey}
                                                    iconLeft={
                                                        <Icon icon="pencil" />
                                                    }
                                                    onClick={toggleModal}
                                                    type="button"
                                                    variant="secondary"
                                                >
                                                    Edit
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <FormLayout.Full>
                                <Textarea
                                    fullWidth
                                    label="Note"
                                    name="note"
                                    onChange={inputChange}
                                    rows={5}
                                    value={inputs.note ?? ""}
                                />
                            </FormLayout.Full>
                        </FormLayout.Grid>
                    )}
                </FormLayout.Section>
            </FormLayout>
            {isDelivery && inputs?.geo_type === "GEOFENCE" ? (
                <GoogleMapComponent
                    coordinates={storeCoordinates}
                    isOpen={modal}
                    onSelect={handleSelectGeofence}
                    selectedShapeGeoJson={inputs.polygon_geo_zone ?? null}
                    storeKey={googleMapKey}
                    toggleModal={toggleModal}
                />
            ) : null}
            {isDelivery && (
                <>
                    <div className={styles.separator} />
                    <FormLayout>
                        <FormLayout.Section title="Fees">
                            <FormLayout.Grid columns={3} maxWidth="twoThirds">
                                <Input
                                    fullWidth
                                    label="Minimum On Cart"
                                    mask={currencyMask(inputs.min ?? "")}
                                    name="min"
                                    onChange={inputChange}
                                    prefix="$"
                                    required
                                    value={inputs.min ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Free After"
                                    mask={currencyMask(inputs.free_after ?? "")}
                                    name="free_after"
                                    onChange={inputChange}
                                    prefix="$"
                                    value={inputs.free_after ?? ""}
                                />
                                <Input
                                    fullWidth
                                    label="Delivery Fee"
                                    mask={currencyMask(
                                        inputs.delivery_fee ?? "",
                                    )}
                                    name="delivery_fee"
                                    onChange={inputChange}
                                    prefix="$"
                                    value={inputs.delivery_fee ?? ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Express Menu">
                            <div className={styles.switchRow}>
                                <div className={styles.switchWithHint}>
                                    <Switch
                                        checked={!!inputs.active}
                                        label="Active"
                                        name="active"
                                        onCheckedChange={
                                            handleExpressActiveToggle
                                        }
                                    />
                                    <Tooltip
                                        content="If enabled, it will allow your customers to place orders from your express menu on an express delivery schedule"
                                        side="top"
                                        triggerHelpCursor
                                        variant="multi"
                                    >
                                        <span
                                            className={styles.infoTrigger}
                                            tabIndex={0}
                                        >
                                            <Icon icon="info" />
                                        </span>
                                    </Tooltip>
                                </div>
                                {inputs.active ? (
                                    <Switch
                                        checked={!!inputs.active_by_default}
                                        label="Active By Default"
                                        name="active_by_default"
                                        onChange={inputChange}
                                    />
                                ) : null}
                            </div>
                            {inputs.active ? (
                                <>
                                    <FormLayout.Grid
                                        columns={3}
                                        maxWidth="twoThirds"
                                    >
                                        <Input
                                            fullWidth
                                            label="Express Name"
                                            name="expressName"
                                            onChange={inputChange}
                                            placeholder="Name"
                                            value={inputs.expressName ?? ""}
                                        />
                                        <TimePicker
                                            fullWidth
                                            label="Start time"
                                            name="start"
                                            onChange={value =>
                                                inputChange({
                                                    target: {
                                                        name: "start",
                                                        value,
                                                    },
                                                })
                                            }
                                            value={inputs.start ?? ""}
                                        />
                                        <TimePicker
                                            fullWidth
                                            label="End time"
                                            name="end"
                                            onChange={value =>
                                                inputChange({
                                                    target: {
                                                        name: "end",
                                                        value,
                                                    },
                                                })
                                            }
                                            value={inputs.end ?? ""}
                                        />
                                    </FormLayout.Grid>
                                    <FormLayout.Grid
                                        columns={2}
                                        maxWidth="twoThirds"
                                    >
                                        {!loadingInventories ? (
                                            inventories?.length > 0 ? (
                                                <Autocomplete
                                                    fullWidth
                                                    label="Inventory Locations"
                                                    maxVisibleChips={1}
                                                    multiple
                                                    name="inventory_provider_ids"
                                                    onChange={
                                                        onChangeInventoryMulti
                                                    }
                                                    options={inventoryOptions}
                                                    value={
                                                        inventorySelectedValues
                                                    }
                                                />
                                            ) : null
                                        ) : (
                                            <div
                                                className={styles.loadingInline}
                                            >
                                                <LoadingProgress
                                                    indeterminate
                                                    indicator="circular"
                                                    size="sm"
                                                    title="Loading inventories"
                                                />
                                            </div>
                                        )}
                                        <Textarea
                                            fullWidth
                                            label="Note"
                                            name="expressNote"
                                            onChange={inputChange}
                                            rows={2}
                                            value={inputs.expressNote ?? ""}
                                        />
                                    </FormLayout.Grid>
                                </>
                            ) : null}
                        </FormLayout.Section>
                    </FormLayout>
                </>
            )}
            <div className={styles.separator} />
            <FormLayout>
                <FormLayout.Section title="Working Hours">
                    <div className={styles.tabs}>
                        <Tabs
                            active={activeTab}
                            className="schedule_form__tabs"
                            navs={renderDaysOfWeekTabs()}
                            onTabChange={setActiveTab}
                        />
                    </div>
                    {showInvalidMessage && (
                        <div className={styles.invalidMessage} role="alert">
                            <span className={styles.invalidMessageIcon}>
                                <Icon icon="info" />
                            </span>
                            <span>{getActiveMessage}</span>
                        </div>
                    )}
                    <div className={styles.footerActions}>
                        <Button
                            disabled={isUpdating}
                            onClick={handleClose}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={isUpdating}
                            onClick={createOrUpdate}
                            variant="primary"
                        >
                            Save
                        </Button>
                    </div>
                </FormLayout.Section>
            </FormLayout>
            <div className={styles.separator} />
        </Form>
    );
};

export default ScheduleForm;
